import { Navigate, Outlet, useLocation } from 'react-router'
import type { JSX } from 'react'
import { FullPageLoading } from '@/ui/states'
import { useAuth } from './context'
import { DEFAULT_HOME, NEXT_PARAM, safeNext, withNext } from './routes'

/**
 * Everything behind it requires a session.
 *
 * `expired` is deliberately *not* treated as anonymous: the re-auth dialog
 * renders over the current screen (`SessionExpired`), and redirecting to
 * `/login` here would unmount the very screen that behaviour exists to keep.
 */
export function RequireAuth(): JSX.Element {
  const { status } = useAuth()
  const location = useLocation()

  if (status === 'loading') return <FullPageLoading />
  if (status === 'anonymous') {
    return <Navigate to={withNext('/login', `${location.pathname}${location.search}`)} replace />
  }
  return <Outlet />
}

/**
 * Sign-in and sign-up, which a signed-in user has no use for.
 *
 * Sent to `next` when there is one, so arriving here from an invitation link
 * while already signed in does not strand the invitation.
 */
export function RequireAnonymous(): JSX.Element {
  const { status } = useAuth()
  const location = useLocation()

  if (status === 'loading') return <FullPageLoading />
  if (status === 'authenticated') {
    const next = safeNext(new URLSearchParams(location.search).get(NEXT_PARAM))
    return <Navigate to={next ?? DEFAULT_HOME} replace />
  }
  return <Outlet />
}
